import { useRef, useState, useEffect, useCallback } from 'react';
import type {
    APIPaginatedResponse,
    CatalogueBulkApproveResponse,
    CatalogueItemActionResponse,
    CatalogueItemVersionID,
    CatalogueServiceBulkApproveItemInfo,
    CatalogueServiceBulkApproveRequest
} from '../types';
import { useStateRef } from './useStateRef';
import { useMessageHandler } from './useMessageHandler';
import type { NewPaginationProps } from '../components/Common/Pagination/Pagination';

export type CatalogueServiceItemAction<ItemId, Item> = (itemId: ItemId, versionId: CatalogueItemVersionID) => Promise<CatalogueItemActionResponse<Item>>;

export interface CatalogueSectionService<
    ItemIdName extends string,
    ItemId,
    Item extends Record<ItemIdName, ItemId> & { versionId: CatalogueItemVersionID },
> {
    itemIdName: ItemIdName;
    pageSize: number;
    itemsLabel: string;
    fetchPage(cursor: string | undefined): Promise<APIPaginatedResponse<Item>>;
    approveItem: CatalogueServiceItemAction<ItemId, Item>;
    rejectItem: CatalogueServiceItemAction<ItemId, Item>;
    bulkApproveItems: (request: CatalogueServiceBulkApproveRequest<ItemIdName, ItemId>) => Promise<CatalogueBulkApproveResponse<ItemIdName, ItemId>>;
}

export const useCatalogueSection = <
    ItemIdName extends string,
    ItemId,
    Item extends Record<ItemIdName, ItemId> & { versionId: CatalogueItemVersionID },
>(service: CatalogueSectionService<ItemIdName, ItemId, Item>) => {
    const serviceRef = useRef(service);
    const handleMessage = useMessageHandler();

    const [items, itemsRef, setItems] = useStateRef<Item[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const cursorRef = useRef<string | undefined>(undefined);
    const isFirstLoadRef = useRef(true);

    const pageSize = service.pageSize;
    const displayStartIndex = (currentPage - 1) * pageSize;
    const displayItems = items.slice(displayStartIndex, displayStartIndex + pageSize);
    const isDisplayFullSize = displayItems.length === pageSize;

    const hasMore = !!cursorRef.current;

    // Fetch more data from backend
    const fetchMore = useCallback(async (cursor?: string) => {
        setLoading(true);

        const response = await serviceRef.current.fetchPage(cursor)

        if (response.ok) {
            const data = response.data;
            cursorRef.current = data.endCursor;
            setItems(prev => [...prev, ...data.items]);
        } else {
            handleMessage({
                error: response.error
            })
        }

        setLoading(false);
    }, [setItems, handleMessage]);

    const needsMoreData = !isDisplayFullSize && hasMore;

    useEffect(() => {
        if (isFirstLoadRef.current || needsMoreData) {
            isFirstLoadRef.current = false;
            fetchMore(cursorRef.current);
        }
    }, [needsMoreData, fetchMore]);

    const approveItem = useCallback(async (itemId: ItemId) => {
        const service = serviceRef.current;
        const itemIdName = service.itemIdName;
        const item = itemsRef.current.find(item => item[itemIdName] === itemId);

        if (!item) {
            return;
        }

        const response = await service.approveItem(itemId, item.versionId);

        if (response.ok) {
            setItems(prev => prev.filter(item => item[itemIdName] !== itemId));

            if (response.messageInfo) {
                handleMessage(response.messageInfo)
            }
        } else {
            handleMessage({
                error: response.error
            })
        }
    }, [itemsRef, setItems, handleMessage]);

    const rejectItem = useCallback(async (itemId: ItemId) => {
        const service = serviceRef.current;
        const itemIdName = service.itemIdName;
        const item = itemsRef.current.find(item => item[itemIdName] === itemId);

        if (!item) {
            return;
        }

        const response = await service.rejectItem(itemId, item.versionId);

        if (response.ok) {
            setItems(prev => prev.filter(item => item[itemIdName] !== itemId));

            if (response.messageInfo) {
                handleMessage(response.messageInfo)
            }
        } else {
            handleMessage({
                error: response.error
            })
        }
    }, [itemsRef, setItems, handleMessage]);

    const approveAllItems = async () => {
        const service = serviceRef.current;
        const displayItemsCount = displayItems.length;

        if (displayItemsCount === 0) {
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to approve all ${displayItemsCount} ${service.itemsLabel}?`
        );

        if (!confirmed) return;

        const itemIdName = service.itemIdName;

        const itemsInfo = displayItems.map(item => ({
            [itemIdName]: item[itemIdName],
            versionId: item.versionId,
        }) as CatalogueServiceBulkApproveItemInfo<ItemIdName, ItemId>);

        const response = await service.bulkApproveItems(itemsInfo);

        if (!response.ok) {
            handleMessage({
                error: response.error,
            })
            return;
        }

        const data = response.data;
        const results = data.results;


        const successfulIds = results.reduce((ids, item) => {
            if (item.success) {
                ids.add(item[itemIdName])
            }

            return ids;
        }, new Set<ItemId>());

        setItems(prev => prev.filter(item => !successfulIds.has(item[itemIdName])));

        if (response.messageInfo) {
            handleMessage(response.messageInfo);
        }
    }

    const curItemsCount = items.length;
    const curPagesCount = Math.ceil(curItemsCount / pageSize);
    const curPageStartIndex = displayStartIndex + 1;
    const curPageEndIndex = displayStartIndex + displayItems.length;

    const pagination: NewPaginationProps = {
        currentPage,
        totalPages: curPagesCount,
        onPageChange: setCurrentPage,
        hasNext: hasMore || currentPage < curPagesCount,
        itemsMessage: curItemsCount > 0
            ? `${curPageStartIndex}–${curPageEndIndex} of ${curItemsCount}${hasMore ? '+' : ''} ${service.itemsLabel}`
            : null
    }

    return {
        items: displayItems,
        loading: loading && displayItems.length === 0,
        approveItem,
        rejectItem,
        approveAllItems,
        pagination
    };
};
